import React, { useRef } from 'react';
import { Box, Button, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { LuCamera, LuImage, LuInfo, LuMail, LuUpload } from 'react-icons/lu';
import { PopoverContent, PopoverRoot, PopoverTrigger } from '@/components/ui/popover';
import CopyableField from '@/components/common/CopyableField';
import { useCurrentHouse } from '@/context/CurrentHouseContext';
import { useAuth } from '@/context/AuthContext.tsx';
import { useHouseIngestEmail } from '@/hooks/house/useHouseIngestEmail';
import { useIsMobileViewport } from '@/hooks/ui/useIsMobileViewport.ts';
import { expandAcceptedFileTypes, MIME_TYPES, SUPPORTED_IMAGE_MIME_TYPES } from '@/utils/upload/uploadUtils.ts';
import {
	COMPOSER_COLLAPSIBLE_PILL_CSS,
	COMPOSER_COLLAPSIBLE_PILL_LABEL_CSS,
	COMPOSER_PORTAL_CLICK_BOUNDARY_PROPS,
} from './composerConstants.ts';
import { useComposerUploadCapabilities } from './useComposerUploadCapabilities.ts';
import { COMPOSER_ACTION_PILL_PROPS } from './composerActionButtonStyles.ts';

// `accept` lists for the hidden file inputs. We scope to types we actually support — notably no
// video — so the native picker doesn't advertise capture we can't ingest. Documents and images are
// split so mobile can give File a documents-only picker (distinct from the dedicated Photo button),
// while desktop's lone File picker accepts both.
const DOCUMENT_MIME_TYPES = [MIME_TYPES.PDF, MIME_TYPES.DOCX, MIME_TYPES.XLSX, MIME_TYPES.TXT, MIME_TYPES.MARKDOWN];
const ACCEPT_DOCUMENTS = DOCUMENT_MIME_TYPES.join(',');
const ACCEPT_IMAGES = expandAcceptedFileTypes(SUPPORTED_IMAGE_MIME_TYPES).join(',');
const ACCEPT_DOCUMENTS_AND_IMAGES = expandAcceptedFileTypes([
	...DOCUMENT_MIME_TYPES,
	...SUPPORTED_IMAGE_MIME_TYPES,
]).join(',');

// Square sizing for icon-only attach buttons so they render circular (1:1) rather than as pills —
// aspectRatio derives width from the button's height regardless of the theme's `sm` height.
const ATTACH_SQUARE_PROPS = { px: 0, minW: 0, aspectRatio: '1' } as const;

interface ComposerAttachActionsProps {
	/**
	 * Files-only by design. This is wired to `record.submitFiles`, whose 2nd param is `isPrivate`;
	 * narrowing to a single arg here means the attach flow can never set privacy — it always falls
	 * back to the composer's `isPrivate` state (which is gated/hidden for users who can't file
	 * privately). Don't add a 2nd argument: a truthy value would silently upload as private.
	 */
	onFilesSelected: (files: FileList | null) => void;
	disabled?: boolean;
}

// Desktop offers File + owner-only Email; mobile swaps in Camera + Photo capture instead.
const MOBILE_ONLY = { base: 'inline-flex', md: 'none' } as const;
const DESKTOP_ONLY = { base: 'none', md: 'inline-flex' } as const;

/**
 * Records-tab attach row: a file picker (plus Camera/Photo capture on mobile)
 * that uploads instantly, and an Email button (desktop) revealing the property's
 * ingest address. Buttons shrink to icon-only on mobile.
 */
const ComposerAttachActions: React.FC<ComposerAttachActionsProps> = (props) => {
	const { onFilesSelected, disabled } = props;

	const { house } = useCurrentHouse();
	const { currentUser } = useAuth();
	const { canUseEmailUpload } = useComposerUploadCapabilities();
	const { email: ingestEmail } = useHouseIngestEmail(house.id, { enabled: canUseEmailUpload });
	const isMobile = useIsMobileViewport();

	// Mobile has dedicated Photo/Camera buttons, so File is documents-only there (opens the Files
	// browser straight away, no redundant photo sheet). Desktop's File is the only picker, so it
	// accepts both.
	const fileAccept = isMobile ? ACCEPT_DOCUMENTS : ACCEPT_DOCUMENTS_AND_IMAGES;

	const fileInputRef = useRef<HTMLInputElement>(null);
	const cameraInputRef = useRef<HTMLInputElement>(null);
	const photoInputRef = useRef<HTMLInputElement>(null);

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onFilesSelected(event.target.files);
		event.target.value = ''; // let the same file be picked again
	};

	return (
		// p gives the button drop-shadows room inside the clip overflowX:auto forces
		// on both axes; the matching negative m offsets it so layout is unchanged.
		<HStack gap={2} flex={1} minW={0} p={1} m={-1} overflowX="auto" css={{ scrollbarWidth: 'none' }}>
			<input ref={fileInputRef} type="file" accept={fileAccept} multiple hidden onChange={handleFileChange} />
			<input
				ref={cameraInputRef}
				type="file"
				accept="image/*"
				capture="environment"
				hidden
				onChange={handleFileChange}
			/>
			<input ref={photoInputRef} type="file" accept={ACCEPT_IMAGES} multiple hidden onChange={handleFileChange} />

			<Button
				{...COMPOSER_ACTION_PILL_PROPS}
				minW={0}
				css={COMPOSER_COLLAPSIBLE_PILL_CSS}
				aria-label="Attach file"
				disabled={disabled}
				onClick={() => fileInputRef.current?.click()}
			>
				<Icon as={LuUpload} boxSize={4} />
				<Text as="span" css={COMPOSER_COLLAPSIBLE_PILL_LABEL_CSS}>
					File
				</Text>
			</Button>

			<Button
				{...COMPOSER_ACTION_PILL_PROPS}
				{...ATTACH_SQUARE_PROPS}
				display={MOBILE_ONLY}
				aria-label="Take photo"
				disabled={disabled}
				onClick={() => cameraInputRef.current?.click()}
			>
				<Icon as={LuCamera} boxSize={4} />
			</Button>

			<Button
				{...COMPOSER_ACTION_PILL_PROPS}
				{...ATTACH_SQUARE_PROPS}
				display={MOBILE_ONLY}
				aria-label="Choose photo"
				disabled={disabled}
				onClick={() => photoInputRef.current?.click()}
			>
				<Icon as={LuImage} boxSize={4} />
			</Button>

			{canUseEmailUpload && (
				<PopoverRoot positioning={{ placement: 'top' }}>
					<PopoverTrigger asChild>
						<Button
							{...COMPOSER_ACTION_PILL_PROPS}
							minW={0}
							display={DESKTOP_ONLY}
							css={COMPOSER_COLLAPSIBLE_PILL_CSS}
							aria-label="Email documents to Bundle"
							disabled={disabled}
						>
							<Icon as={LuMail} boxSize={4} />
							<Text as="span" css={COMPOSER_COLLAPSIBLE_PILL_LABEL_CSS}>
								Email
							</Text>
						</Button>
					</PopoverTrigger>
					<PopoverContent
						{...COMPOSER_PORTAL_CLICK_BOUNDARY_PROPS}
						borderRadius="2xl"
						boxShadow="lg"
						w="440px"
						maxW="calc(100vw - 32px)"
						p={4}
					>
						<VStack align="stretch" gap={3}>
							<Box>
								<Text fontSize="sm" fontWeight="semibold">
									Forward emails to Bundle
								</Text>
								<Text fontSize="xs" color="fg.muted">
									Attachments uploaded only — the email body is ignored
								</Text>
							</Box>

							{ingestEmail ? (
								<CopyableField value={ingestEmail} truncate />
							) : (
								<Text fontSize="xs" color="fg.muted">
									No upload email is available yet.
								</Text>
							)}

							{currentUser?.email && (
								<HStack gap={2} align="center" color="fg.muted">
									<Icon as={LuInfo} boxSize={4} mt={0.5} flexShrink={0} />
									<Text fontSize="xs" lineHeight="1.4">
										Must be sent from{' '}
										<Text as="span" fontWeight="semibold" color="brand.solid">
											{currentUser.email}
										</Text>
									</Text>
								</HStack>
							)}
						</VStack>
					</PopoverContent>
				</PopoverRoot>
			)}
		</HStack>
	);
};

export default ComposerAttachActions;
